import { useState } from 'react';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import { isWorkEmail } from '../../utils/emailValidation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvitesSent?: () => void; // Callback for when invites are successfully sent
}

const InviteModal = ({ isOpen, onClose, onInvitesSent }: InviteModalProps) => {
  const [emails, setEmails] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);
  const { post } = useApi();
  const { captureEvent } = useAnalytics();

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const setCurrentEmail = (value: string, index: number) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    if (index === emails.length - 1 && value && emails.length < 10) {
      newEmails.push('');
    }
    if (!value) {
      while (newEmails.length > 1 && !newEmails[newEmails.length - 1]) {
        newEmails.pop();
      }
      if (newEmails.length < 10 && newEmails[newEmails.length - 1] !== '') {
        newEmails.push('');
      }
    }
    setEmails(newEmails);
  };

  const handleSendInvites = async () => {
    const nonEmptyEmails = emails.filter(email => email.trim() !== '');
    const invalidEmails = nonEmptyEmails.filter(email => !validateEmail(email));
    
    if (invalidEmails.length > 0) {
      toast.error(`Invalid emails: ${invalidEmails.join(', ')}`, toastConfig);
      return;
    }
    
    if (nonEmptyEmails.length === 0) {
      toast.error("No emails entered", toastConfig);
      return;
    }

    // Validate work email domains
    const freeEmailAddresses = nonEmptyEmails.filter(email => !isWorkEmail(email));
    if (freeEmailAddresses.length > 0) {
      toast.error(`Please use work email addresses only. Free email providers not allowed: ${freeEmailAddresses.join(', ')}`, toastConfig);
      return;
    }

    setLoading(true);
    try {
      const response = await post('/invite/', { emails: nonEmptyEmails });
      setEmails(['']);
      onClose();
      toast.success('Invites sent!', toastConfig);
      captureEvent('sent_invite', {
        emails: nonEmptyEmails
      });
      if (onInvitesSent) {
        onInvitesSent();
      }
      return response.data;
    } catch (error: any) {
      console.error('Error sending invite:', error);
      // Check if error is about work email requirement
      if (error.detail && 
          (error.detail.includes('work email') || 
           error.detail.includes('Free email providers'))) {
        toast.error(error.detail, toastConfig);
      } else {
        toast.error(`Error sending invite. Please contact support@aimdoc.ai if the problem persists.`, toastConfig);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmails(['']);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite members to your team!</DialogTitle>
          <DialogDescription>
            Enter email addresses to send invitations to join your organization.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {emails.map((email, index) => {
            const hasError = email && (!validateEmail(email) || !isWorkEmail(email));
            return (
              <div key={index}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setCurrentEmail(e.target.value, index)}
                  placeholder={`Add new email i.e ${(index + 1) % 2 === 0 ? 'cam@aimdoc.ai' : 'ben@aimdoc.ai'}`}
                  className={hasError ? 'border-red-500' : ''}
                />
                {email && !isWorkEmail(email) && validateEmail(email) && (
                  <p className="text-xs text-red-500 mt-1">Please use a work email address</p>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendInvites}
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              'Send Invites'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteModal; 